import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, IndianRupee, Sparkles, ShieldCheck, BarChart3, ChevronRight } from "lucide-react";

const BENEFITS = [
  { Icon: ShieldCheck,  title: "Reach vetted international travelers", body: "Tap into a curated audience of foreign tourists actively planning India trips." },
  { Icon: Sparkles,     title: "Just 10% flat marketplace fee",        body: "Listed on top of your price. You keep 100% of your base price." },
  { Icon: BarChart3,    title: "Operations dashboard included",       body: "KPIs, monthly trend chart, top listings & exports — all in one place." },
];

const STEPS = [
  { n: "01", t: "Create your profile",  d: "Tell travelers about your business, cities you serve, and contact info." },
  { n: "02", t: "Publish listings",      d: "Add photos, inclusions, availability dates and capacity per slot." },
  { n: "03", t: "Get booked",            d: "Bookings arrive in your dashboard with guest details, dates and payouts." },
  { n: "04", t: "Grow with insights",    d: "Use KPIs, ratings and exports to improve and scale." },
];

const FAQ = [
  { q: "How does the 10% marketplace fee work?",                  a: "You set your base price. We add 10% on top, which is what the customer pays. That 10% is our platform revenue." },
  { q: "How do I get paid?",                                       a: "In V1, payouts to vendors are handled offline (bank transfer/cheque) at the end of each cycle. We're integrating automatic payouts next." },
  { q: "What's the cancellation policy?",                          a: "You define a free-cancellation window per listing (e.g. 24h/48h/7d). Customers see this on the listing before booking." },
  { q: "Are international cards & UPI supported?",                 a: "Yes — international cards via Stripe (USD). Indian payment methods (UPI/cards/netbanking) via Razorpay (INR)." },
  { q: "Can I list multiple offerings?",                           a: "Absolutely. You can publish as many experiences as you want — each with its own availability calendar and pricing." },
];

const TESTIMONIALS = [
  { name: "Aarav P.", title: "Founder, Spiti Overland",      quote: "We saw 3x more international leads in our first month — IndoVoyage just gets foreign travelers.", img: "https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?w=200" },
  { name: "Riya K.",  title: "Owner, Backwaters Houseboats", quote: "The transparent fee builds trust. Bookings convert better than any other platform we've tried.",      img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200" },
  { name: "Vikram S.", title: "Lead Guide, Himalaya Tours",  quote: "The vendor dashboard is the best part — I finally have one place that shows my whole business.",     img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200" },
];

export default function BecomeVendor() {
  const { user } = useAuth();
  const nextHref = !user ? "/auth/register?role=vendor" : user.role === "vendor" ? "/vendor" : "/vendor/onboarding";

  return (
    <div data-testid="become-vendor-page" className="iv-pattern-mandala">
      {/* Hero */}
      <section className="relative overflow-hidden iv-hero-grad">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-14 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full bg-[hsl(var(--saffron))] text-white">For local hosts & DMCs</span>
            <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Reach curious <span className="bg-gradient-to-r from-[hsl(var(--saffron))] via-[hsl(var(--vermilion))] to-[hsl(var(--rose-indian))] bg-clip-text text-transparent">international travelers</span>
            </h1>
            <p className="mt-3 text-muted-foreground max-w-xl text-lg">List your experience on IndoVoyage. Transparent pricing, real-time insights, and only a flat 10% marketplace fee — added on top of your price so you get exactly what you list.</p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm max-w-xl">
              {["No onboarding or monthly fee", "You keep 100% of your base price", "Built-in availability & cancellations", "Dashboard with revenue, ratings & exports"].map((t) => (
                <li key={t} className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t}</li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={nextHref}><Button className="rounded-xl h-12 px-6 bg-gradient-to-r from-[hsl(var(--vermilion))] to-[hsl(var(--saffron))] text-white hover:brightness-110" data-testid="become-vendor-cta">Start onboarding <ChevronRight className="h-4 w-4 ml-1" /></Button></Link>
              <a href="#faq"><Button variant="ghost" className="rounded-xl h-12">Read FAQ</Button></a>
            </div>
          </div>
          <div className="hidden lg:block relative">
            <img src="https://images.unsplash.com/photo-1520454974749-611b7248ffdb?w=1600" alt="Host" className="rounded-3xl w-full h-[480px] object-cover shadow-[0_30px_60px_-30px_rgba(31,42,68,0.5)]" />
            <div className="absolute -bottom-5 -left-5 rounded-2xl bg-card p-4 border border-border shadow-lg max-w-xs">
              <div className="text-xs text-muted-foreground">Marketplace fee</div>
              <div className="font-display text-2xl font-semibold">10% flat</div>
              <div className="text-xs text-muted-foreground">Added on top of your price. No hidden charges.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-center">Why hosts choose IndoVoyage</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-2xl bg-card border border-border p-6">
                <span className="h-11 w-11 rounded-xl bg-gradient-to-br from-[hsl(var(--saffron))] to-[hsl(var(--vermilion))] text-white flex items-center justify-center"><b.Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-display text-xl font-semibold">{b.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing strip */}
      <section className="py-12 iv-pattern-rangoli">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <div className="rounded-3xl bg-card border border-border p-8 grid md:grid-cols-3 items-center gap-6">
            <div className="md:col-span-2">
              <h3 className="font-display text-2xl font-semibold">Simple, transparent pricing</h3>
              <p className="text-muted-foreground mt-1">A flat <b>10% marketplace fee</b> is added on top of your base price. Customers see the all-inclusive price. You always receive 100% of your base.</p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-xl bg-muted p-3"><div className="text-xs text-muted-foreground">Your base price</div><div className="font-semibold">$100</div></div>
                <div className="rounded-xl bg-muted p-3"><div className="text-xs text-muted-foreground">+10% marketplace fee</div><div className="font-semibold">$10</div></div>
                <div className="rounded-xl bg-[hsl(var(--emerald-india))]/10 p-3"><div className="text-xs text-muted-foreground">Customer pays total</div><div className="font-semibold">$110</div></div>
              </div>
            </div>
            <Link to={nextHref}><Button className="rounded-xl h-12 w-full bg-gradient-to-r from-[hsl(var(--peacock))] to-[hsl(var(--emerald-india))] text-white">Get started free</Button></Link>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <h2 className="font-display text-3xl font-semibold tracking-tight">From signup to first booking</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-2xl bg-card border border-border p-5">
                <div className="text-xs font-semibold text-[hsl(var(--vermilion))]">{s.n}</div>
                <h3 className="mt-1 font-display text-lg font-semibold">{s.t}</h3>
                <p className="text-muted-foreground text-sm mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Hosts love IndoVoyage</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="rounded-2xl bg-card border border-border p-6">
                <p className="text-sm leading-relaxed">“{t.quote}”</p>
                <div className="mt-4 flex items-center gap-3">
                  <img src={t.img} alt="" className="h-10 w-10 rounded-full object-cover" />
                  <div><div className="text-sm font-semibold">{t.name}</div><div className="text-xs text-muted-foreground">{t.title}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-12">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-center">Frequently asked questions</h2>
          <div className="mt-6 grid gap-2">
            {FAQ.map((f, i) => (
              <details key={`faq-${i}-${f.q.substring(0, 20).replace(/\s+/g, '-')}`} className="rounded-2xl bg-card border border-border p-5">
                <summary className="font-semibold cursor-pointer">{f.q}</summary>
                <p className="text-muted-foreground text-sm mt-2">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={nextHref}><Button className="rounded-xl h-12 px-6 bg-gradient-to-r from-[hsl(var(--vermilion))] to-[hsl(var(--saffron))] text-white" data-testid="become-vendor-cta-bottom">Become a vendor</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
